import React from "react";
import i18next from "i18next";
import {
	Routes, Route, useNavigate, Outlet,
	useMatch, useLocation,
} from "react-router-dom";
import { SnackbarProvider } from 'notistack';
import { ThemeProvider, CssBaseline, GlobalStyles } from "@mui/material";

import { SiteAuth } from '../auth';
import { SiteHeader } from "./Header";

import theme from "../theme";
import { SiteFooter } from "../components/AppFooter";
import { ymHit } from "@haohan-frontend/metrika";

const SiteHome = React.lazy(
	() => import(
		/* webpackChunkName: "home-page" */
		'../views/Home'
	).then(m => ({default: m.SiteHome}))
);

const CharactersSearch = React.lazy(
	() => import(
		/* webpackChunkName: "characters-search-page" */
		'../views/CharactersSearch'
	).then(m => ({default: m.CharactersSearch}))
);

const CharacterItem = React.lazy(
	() => import(
		/* webpackChunkName: "characters-item-page" */
		'../views/CharacterItem'
	).then(m => ({default: m.CharacterItem}))
);

const Games = React.lazy(
	() => import(
		/* webpackChunkName: "games-page" */
		'../views/Games'
	).then(m => ({default: m.Games}))
);

const GameItem = React.lazy(
	() => import(
		/* webpackChunkName: "game-item-page" */
		'../views/GameItem'
	).then(m => ({default: m.GameItem}))
);
const GameSession = React.lazy(
	() => import(
		/* webpackChunkName: "game-session-page" */
		'../views/GameSession'
	).then(m => ({default: m.GameSessionView}))
);
// const GameCreate = React.lazy(
// 	() => import(
// 		/* webpackChunkName: "game-create-page" */
// 		'../views/GameCreate'
// 	).then(m => ({default: m.GameCreate}))
// );

const Lessons = React.lazy(
	() => import(
		/* webpackChunkName: "lessons-page" */
		'../views/Lessons'
	)
		.then(m => ({default: m.Lessons}))
);

const LessonItem = React.lazy(
	() => import(
		/* webpackChunkName: "lesson-item-page" */
		'../views/LessonItem'
	).then(m => ({default: m.LessonItem}))
);

const RadicalTable = React.lazy(
	() => import(
		/* webpackChunkName: "radical-table-page" */
		'../views/RadicalTable'
	).then(m => ({default: m.RadicalsTable}))
);

const Worksheet = React.lazy(
	() => import(
		/* webpackChunkName: "worksheet-collection-page" */
		'../views/Worksheet/WorksheetCollection'
	).then(m => ({default: m.WorksheetCollection}))
);

const WorksheetItem = React.lazy(
	() => import(
		/* webpackChunkName: "worksheet-item-page" */
		'../views/Worksheet/WorksheetItem'
	).then(m => ({default: m.WorksheetItem}))
);

import { CacheProvider } from "@emotion/react";
import createCache, { type EmotionCache } from "@emotion/cache";
import { useSsrQuery } from "../../ssr";
import { AuthActiveUserDTO } from "@haohan/papi-dto";
import { AxiosError } from "axios";
import { AppStoreContext } from "../AppStore";
import { MyRoot } from "../my";
import { makeStyles } from "tss-react/mui";
import { ErrorBoundary } from "./ErrorBoundary";
import { plainToInstance } from "class-transformer";

if (typeof window !== "undefined" && window['_APP_LANG']) {
	i18next.changeLanguage(window['_APP_LANG']);
}

let appCache: EmotionCache | undefined = undefined;

export const createAppCache = () =>
    appCache = createCache({ 
        "key": "css"
    });

export function SiteApp() {
	const location = useLocation();
	const navigate = useNavigate();
	const matches = useMatch({path: "/:lang", end: false});
	let lang = matches?.params?.lang;
	if (!['ru', 'en'].includes(lang)) {
		lang = null;
	}
	React.useEffect(() => {
		ymHit(location.pathname + location.search);
	}, [location.pathname]);

	const { data: activeUser, refetch } = useSsrQuery(
		"auth.activeUser",
		async (axios) => {
			try {
				const r = await axios.get<AuthActiveUserDTO>("/api/auth/session/");
				return r.data;
			} catch (err) {
				const e = err as AxiosError;
				if (e.response.status === 401) {
					return null;
				}
				throw err;
			}
		}
	);

	React.useEffect(() => {
		if (!['ru', 'en'].includes(lang)) {
			navigate(
				{
					pathname: `/ru${location.pathname}`,
					search: location.search
				},
				{replace: true}
			);
			i18next.changeLanguage('ru');
		}
	}, [lang]);
	let routesEl: React.ReactNode = null;
	if (lang) {
		routesEl = (
			<>
				<Route path="*" element={<div>NOT FOUND</div>} />
				<Route path="my/*" element={<MyRoot/>} />
				<Route index element={
					<React.Suspense>
						<SiteHome/>
				  </React.Suspense>
				}/>
				<Route path="characters" element={
					<React.Suspense>
						<CharactersSearch/>
					</React.Suspense>
				}/>
				<Route path="character/:symbol" element={
					<React.Suspense>
						<CharacterItem/>
					</React.Suspense>
				}/>
				<Route path="games/" element={
					<React.Suspense>
						<Games/>
					</React.Suspense>
				}/>
				<Route path="game/:publicHash" element={
					<React.Suspense>
						<GameItem/>
					</React.Suspense>
				}/>
				<Route path="games/session/:publicHash" element={
					<React.Suspense>
						<GameSession/>
					</React.Suspense>
				}/>
				<Route path="lessons/" element={
					<React.Suspense>
						<Lessons/>
					</React.Suspense>
				}/>
				<Route path="lessons/:id/" element={
					<React.Suspense>
						<LessonItem/>
					</React.Suspense>
				}/>
				<Route path="radicals" element={
					<React.Suspense>
						<RadicalTable/>
					</React.Suspense>
				}/>
				<Route path="worksheet" element={
					<React.Suspense>
						<Worksheet/>
					</React.Suspense>
				}/>
				<Route path="worksheet/:id" element={
					<React.Suspense>
						<WorksheetItem/>
					</React.Suspense>
				}/>
				{/*{props.routes.map(r => {
					return {
						...r,
						path: r.path
					};
				}).map(r => (
					<Route
						key={r.path}
						path={r.path}
						element={r.element}
					/>
				))}*/}
			</>
		);
	}

	return (
		
		<ThemeProvider theme={theme()}>
			<AppStoreContext.Provider
				value={{
					activeUser: plainToInstance(AuthActiveUserDTO, activeUser),
					refetchUser: refetch
				}}>
				<CssBaseline />
				<GlobalStyles styles={{
					'html, body': {
						height: '100%'
					},
					'#root': {
						// overflow: 'hidden',
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
					},
					'[data-rsbs-header]': {
						boxShadow: 'none !important'
					},
					'[data-rsbs-footer]': {
						boxShadow: 'none !important'
					},
				}}/>
				<CacheProvider value={appCache ?? createAppCache()}>
					<Inner>
						<Routes>
							<Route
								path={`/${lang}`}
								element={<Outlet/>}
							>
								<Route path="auth/*" element={<SiteAuth/>}/>
								<Route element={(
									<>
										<SiteHeader />
										<main
											style={{
												width: '100%',
												flex: 1,
											}}
										>
											<Outlet/>
										</main>
										<SiteFooter />
									</>
								)}>
									{routesEl}
								</Route>
							</Route>
						</Routes>
					</Inner>
				</CacheProvider>
			</AppStoreContext.Provider>
		</ThemeProvider>
	);
}

const useStyles = makeStyles()(theme => {
	return {
		root: {
			boxShadow: theme.shadows[3],
			borderRadius: 6,
			backgroundColor: theme.palette.background.paper,
			padding: '12px 8px',
			display: 'flex',
			fontSize: 14,
			paddingBottom: 6
		},
		iconSuccess: {
			paddingTop: 3,
			color: theme.palette.success.main
		},
		container: {
			'& .notistack-MuiContent': {
				background: theme.palette.background.paper,
				color: theme.palette.text.primary
			}
		}
	};
})

export const Inner: React.FC<React.PropsWithChildren> = props => {
	const { classes } = useStyles();
	return (
		<ErrorBoundary>
			<SnackbarProvider Components={{
				success: React.forwardRef<HTMLDivElement>((p, ref) => {
					const icon = p["iconVariant"]["success"];
					return (
						<div ref={ref} className={classes.root}>
							<div className={classes.iconSuccess}>
								{icon}
							</div>
							<div>
								{p['message']}
							</div>
						</div>
					);
				})
			}}>
				{props.children}
			</SnackbarProvider>
		</ErrorBoundary>
	);
}
